/**
 * Nelder-Mead simplex optimization algorithm.
 * Inlined from fmin@0.0.2 (unmaintained) — only this single function was used.
 * @see https://en.wikipedia.org/wiki/Nelder%E2%80%93Mead_method
 */

export interface NelderMeadResult {
	/** Best result of the objective function */
	fx: number;
	/** Arguments that produced the best result */
	x: number[];
}

interface NelderMeadParameters {
	maxIterations?: number;
	nonZeroDelta?: number;
	zeroDelta?: number;
	minErrorDelta?: number;
	minTolerance?: number;
	rho?: number;
	chi?: number;
	psi?: number;
	sigma?: number;
}

function weightedSum(ret: number[], w1: number, v1: number[], w2: number, v2: number[]): void {
	for (let j = 0; j < ret.length; j++) {
		ret[j] = w1 * v1[j]! + w2 * v2[j]!;
	}
}

type SimplexPoint = number[] & { fx: number };

/** Minimizes a function using the downhill simplex (Nelder-Mead) method. */
export function nelderMead(
	f: (x: number[]) => number,
	x0: number[],
	parameters?: NelderMeadParameters
): NelderMeadResult {
	const p = parameters ?? {};

	const maxIterations = p.maxIterations ?? x0.length * 200;
	const nonZeroDelta = p.nonZeroDelta ?? 1.05;
	const zeroDelta = p.zeroDelta ?? 0.001;
	const minErrorDelta = p.minErrorDelta ?? 1e-6;
	const minTolerance = p.minTolerance ?? 1e-5;
	const rho = p.rho ?? 1;
	const chi = p.chi ?? 2;
	const psi = p.psi ?? -0.5;
	const sigma = p.sigma ?? 0.5;

	const N = x0.length;
	const simplex = Array.from<SimplexPoint>({ length: N + 1 });

	// Initialize simplex
	const initial = x0.slice() as SimplexPoint;
	initial.fx = f(x0);
	simplex[0] = initial;

	for (let i = 0; i < N; i++) {
		const point = x0.slice() as SimplexPoint;
		point[i] = point[i] ? point[i]! * nonZeroDelta : zeroDelta;
		point.fx = f(point);
		simplex[i + 1] = point;
	}

	function updateSimplex(value: SimplexPoint): void {
		for (let i = 0; i < value.length; i++) {
			simplex[N]![i] = value[i]!;
		}
		simplex[N]!.fx = value.fx;
	}

	const sortOrder = (a: SimplexPoint, b: SimplexPoint) => a.fx - b.fx;

	const centroid = x0.slice();
	const reflected = x0.slice() as SimplexPoint;
	const contracted = x0.slice() as SimplexPoint;
	const expanded = x0.slice() as SimplexPoint;

	for (let iteration = 0; iteration < maxIterations; iteration++) {
		simplex.sort(sortOrder);

		let maxDiff = 0;
		for (let i = 0; i < N; i++) {
			maxDiff = Math.max(maxDiff, Math.abs(simplex[0]![i]! - simplex[1]![i]!));
		}

		if (Math.abs(simplex[0]!.fx - simplex[N]!.fx) < minErrorDelta && maxDiff < minTolerance) {
			break;
		}

		// Compute centroid of all but worst point
		for (let i = 0; i < N; i++) {
			centroid[i] = 0;
			for (let j = 0; j < N; j++) {
				centroid[i]! += simplex[j]![i]!;
			}
			centroid[i]! /= N;
		}

		// Reflect worst point past centroid
		const worst = simplex[N]!;
		weightedSum(reflected, 1 + rho, centroid, -rho, worst);
		reflected.fx = f(reflected);

		if (reflected.fx < simplex[0]!.fx) {
			// Reflected is best seen — try expanding
			weightedSum(expanded, 1 + chi, centroid, -chi, worst);
			expanded.fx = f(expanded);
			updateSimplex(expanded.fx < reflected.fx ? expanded : reflected);
		} else if (reflected.fx >= simplex[N - 1]!.fx) {
			// Reflected is worse than second worst — contract
			let shouldReduce = false;

			if (reflected.fx > worst.fx) {
				// Inside contraction
				weightedSum(contracted, 1 + psi, centroid, -psi, worst);
				contracted.fx = f(contracted);
				if (contracted.fx < worst.fx) {
					updateSimplex(contracted);
				} else {
					shouldReduce = true;
				}
			} else {
				// Outside contraction
				weightedSum(contracted, 1 - psi * rho, centroid, psi * rho, worst);
				contracted.fx = f(contracted);
				if (contracted.fx < reflected.fx) {
					updateSimplex(contracted);
				} else {
					shouldReduce = true;
				}
			}

			if (shouldReduce) {
				if (sigma >= 1) break;
				for (let i = 1; i < simplex.length; i++) {
					weightedSum(simplex[i]!, 1 - sigma, simplex[0]!, sigma, simplex[i]!);
					simplex[i]!.fx = f(simplex[i]!);
				}
			}
		} else {
			updateSimplex(reflected);
		}
	}

	simplex.sort(sortOrder);
	return { fx: simplex[0]!.fx, x: simplex[0]!.slice() };
}
